/**
 * Browser-based Migration Script: Move Notes from Document Fields to Subcollection
 * 
 * This script migrates all notes stored in the 'notes' field of store documents
 * to the 'notes' subcollection (Stores/{storeId}/notes/{noteId})
 * 
 * To run this migration:
 * 1. Open your browser's developer console (F12)
 * 2. Navigate to any page in the app (so you're authenticated)
 * 3. Copy and paste this entire function into the console
 * 4. Call: migrateNotesInBrowser()
 * 
 * The migration will run with your current authentication context.
 */

export async function migrateNotesInBrowser() {
  // Import Firebase functions dynamically
  const { 
    collection, 
    getDocs, 
    doc, 
    getDoc, 
    setDoc, 
    updateDoc,
    Timestamp,
    query,
    orderBy,
    deleteField
  } = await import('firebase/firestore');
  
  // Get the db instance from the app
  const { db } = await import('../config/firebase');
  
  interface Note {
    id?: string;
    text: string;
    date?: string;
    time?: string;
    author?: string;
    salesRepName?: string;
    managerName?: string;
    competitors?: string[];
    media?: any[];
    documents?: any[];
    nextVisitDate?: string;
    replies?: Note[];
    createdAt?: Timestamp | Date;
  }

  try {
    console.log('🚀 Starting notes migration...');
    console.log('📋 Fetching all stores...');
    const storesRef = collection(db, 'Stores');
    const storesSnapshot = await getDocs(storesRef);
    
    let totalStores = 0;
    let storesWithNotes = 0;
    let totalNotesMigrated = 0;
    let totalErrors = 0;
    let storesCleaned = 0;
    
    for (const storeDoc of storesSnapshot.docs) {
      totalStores++;
      const storeId = storeDoc.id;
      const storeData = storeDoc.data();
      
      // Check if store has notes in the document field
      if (storeData.notes && Array.isArray(storeData.notes) && storeData.notes.length > 0) {
        storesWithNotes++;
        console.log(`\n📦 Processing store ${storeId} with ${storeData.notes.length} notes...`);
        
        // Check if notes subcollection already exists
        const notesCollectionRef = collection(db, 'Stores', storeId, 'notes');
        const existingNotesSnapshot = await getDocs(notesCollectionRef);
        const existingNoteIds = new Set(existingNotesSnapshot.docs.map(doc => doc.id));
        
        // Migrate each note
        for (let i = 0; i < storeData.notes.length; i++) {
          const note = storeData.notes[i] as Note;
          
          try {
            // Generate note ID if not present
            const noteId = note.id || `note-${Date.now()}-${i}`;
            
            // Skip if note already exists in subcollection
            if (existingNoteIds.has(noteId)) {
              console.log(`  ⏭️  Note ${noteId} already exists in subcollection, skipping...`);
              continue;
            }
            
            // Prepare note data
            const noteData: any = {
              text: note.text || '',
              date: note.date || new Date().toLocaleDateString(),
              time: note.time || new Date().toLocaleTimeString(),
              author: note.author || note.salesRepName || note.managerName || 'Unknown',
              salesRepName: note.salesRepName || note.author || 'Unknown',
              managerName: note.managerName || note.author || 'Unknown',
              competitors: note.competitors || [],
              media: note.media || [],
              documents: note.documents || [],
              nextVisitDate: note.nextVisitDate || '',
              replies: note.replies || [],
            };
            
            // Handle createdAt
            if (note.createdAt) {
              if (note.createdAt instanceof Timestamp) {
                noteData.createdAt = note.createdAt;
              } else if (note.createdAt instanceof Date) {
                noteData.createdAt = Timestamp.fromDate(note.createdAt);
              } else {
                noteData.createdAt = Timestamp.now();
              }
            } else {
              // Try to derive from date/time if available
              if (note.date) {
                try {
                  const dateParts = note.date.split('/');
                  if (dateParts.length === 3) {
                    const [month, day, year] = dateParts;
                    const dateObj = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                    if (note.time) {
                      const timeParts = note.time.split(':');
                      if (timeParts.length >= 2) {
                        dateObj.setHours(parseInt(timeParts[0]), parseInt(timeParts[1]));
                      }
                    }
                    noteData.createdAt = Timestamp.fromDate(dateObj);
                  } else {
                    noteData.createdAt = Timestamp.now();
                  }
                } catch {
                  noteData.createdAt = Timestamp.now();
                }
              } else {
                noteData.createdAt = Timestamp.now();
              }
            }
            
            // Save note to subcollection
            const noteDocRef = doc(notesCollectionRef, noteId);
            await setDoc(noteDocRef, noteData);
            
            totalNotesMigrated++;
            console.log(`  ✅ Migrated note ${noteId}`);
          } catch (error: any) {
            totalErrors++;
            console.error(`  ❌ Error migrating note ${i} in store ${storeId}:`, error.message);
          }
        }
        
        // After processing all notes, remove the old notes field from the store document
        // Only do this if all notes were successfully migrated or already exist
        try {
          const storeDocRef = doc(db, 'Stores', storeId);
          const storeDocSnap = await getDoc(storeDocRef);
          
          if (storeDocSnap.exists()) {
            const storeData = storeDocSnap.data();
            // Check if notes field exists and we have notes in subcollection
            if (storeData.notes && Array.isArray(storeData.notes)) {
              // Get current count from subcollection
              const notesCollectionRef = collection(db, 'Stores', storeId, 'notes');
              const notesSnapshot = await getDocs(notesCollectionRef);
              const subcollectionCount = notesSnapshot.size;
              const documentFieldCount = storeData.notes.length;
              
              // Remove the old notes field if we have at least as many notes in subcollection
              // This is safe because all notes should already be migrated
              if (subcollectionCount >= documentFieldCount) {
                // Remove the old notes field
                await updateDoc(storeDocRef, {
                  notes: deleteField()
                });
                storesCleaned++;
                console.log(`  🧹 Removed old notes field from store document (${documentFieldCount} notes in field, ${subcollectionCount} in subcollection)`);
              } else {
                console.log(`  ⚠️  Not removing notes field - subcollection has ${subcollectionCount} notes but document has ${documentFieldCount}`);
              }
            }
          }
        } catch (error: any) {
          console.error(`  ⚠️  Could not remove notes field from store ${storeId}:`, error.message);
        }
      }
    }
    
    console.log('\n=== Migration Summary ===');
    console.log(`Total stores processed: ${totalStores}`);
    console.log(`Stores with notes: ${storesWithNotes}`);
    console.log(`Total notes migrated: ${totalNotesMigrated}`);
    console.log(`Stores cleaned (old notes field removed): ${storesCleaned}`);
    console.log(`Total errors: ${totalErrors}`);
    console.log('✅ Migration completed!');
    
    return {
      success: true,
      totalStores,
      storesWithNotes,
      totalNotesMigrated,
      storesCleaned,
      totalErrors
    };
    
  } catch (error: any) {
    console.error('❌ Migration failed:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

// Make it available globally for console access
if (typeof window !== 'undefined') {
  (window as any).migrateNotesInBrowser = migrateNotesInBrowser;
}

